import { createElement, useMemo } from "react";
import { useKeyDatesList } from "../keydates/KeyDatesContext";
import { statementText } from "../statements/statementKinds";
import { usePhaseAdd } from "../today/usePhaseAdd";
import { useTaskActions } from "./TaskActionsContext";
import { useTaskLinkLists } from "./TaskLinksContext";
import { useTickTasks } from "./useTickTasks";
import { TaskListRow } from "./TaskListRow";
import { linksOfTask } from "./taskLinks";

// What a task is linked to besides its goal (linksOfTask): its Key Date, dream and fear. Every
// task list says it the same way.
export function useTaskLinksOf() {
  const keyDates = useKeyDatesList();
  const lists = useTaskLinkLists(); // null outside NudgeApp: nothing to name
  const dreams = lists?.dreams.items, fears = lists?.fears.items;
  const names = useMemo(() => ({
    keyDateId: new Map((keyDates || []).map(k => [k.id, k.title])),
    dreamId: new Map((dreams || []).map(d => [d.id, d.title])),
    fearId: new Map((fears || []).map(f => [f.id, statementText("fears", f)])),
  }), [keyDates, dreams, fears]);
  // leaveOut is the field of the link already named above the row (keyDateId under a Key Date).
  return (task, leaveOut = null) => linksOfTask(leaveOut ? { ...task, [leaveOut]: null } : task, names);
}

// Task rows as the Tasks page draws them, for Radar, Key Dates, the Dreamboard and Fears: row(task, { withGoal, showDue, leaveOut })
// ticks one off (greyed while it saves), opens it, and puts it in today. `toast` is Add to today's Undo message; the page
// draws it once. newTask(defaults) and openTask(task) open the task editor; addNow(task) is Add to today. linkTasks(tasks)
// is what LinkedTasks needs under a dream or fear. `leaveOut` is the field of the link the list sits under (keyDateId,
// dreamId, fearId): its name is already above the rows, so they leave it out and keep the rest. (The Tasks and Goals pages still take these as props.)
export function useTaskRows(goals) {
  const { openTask, newTask, saveTask, phase } = useTaskActions();
  const phaseAdd = usePhaseAdd(saveTask, phase);
  const { isTicked, tick } = useTickTasks((task) => saveTask(task.id, { done: true }));
  const linksOf = useTaskLinksOf();
  const goalById = useMemo(() => new Map((goals || []).map(g => [g.id, g])), [goals]);
  const row = (task, { withGoal = true, showDue = true, leaveOut = null } = {}) => createElement(TaskListRow, {
    key: task.id, task, goal: withGoal ? goalById.get(task.parentGoalId) || null : null, showDue,
    links: linksOf(task, leaveOut), done: isTicked(task.id), onDone: tick, onOpen: openTask, onAddToday: phaseAdd.addNow,
  });
  const linkTasks = (tasks) => ({ tasks, row: (t, field) => row(t, { leaveOut: field }), onAddTask: (link, id) => newTask({ [link.field]: id }) });
  return { row, newTask, openTask, addNow: phaseAdd.addNow, toast: phaseAdd.toast, linkTasks };
}
